import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { listAreas2, listLocations, listOrgUnits, listLocationLaborTargets, listUsers } from 'graphql/queries';
import { createMissingOrgUnits } from 'utils/storeMigration/orgUnitVerification';
import { migrationPresets } from 'utils/storeMigration/runMigration';
import { cleanupAllManagerAssignments } from 'utils/storeMigration/cleanupManagerAssignments';
import { 
  migrateLocationsOnly, 
  migrateLaborTargetsOnly, 
  migrateHistoryOnly,
  cleanLocationMetadata,
  getMigrationCounts
} from 'utils/storeMigration/individualMigrationSteps';
import { migrateUsersToEmployees, validateUserEmployeeMigration } from 'utils/storeMigration/employeeMigration';
import { migrateLocationManagers } from 'utils/storeMigration/locationEmployeeMigration';
import { migrateOrgUnitManagers } from 'utils/storeMigration/orgUnitEmployeeMigration';
import { createRoleDefinitions, checkRequiredRolesExist } from 'utils/storeMigration/roleDefinitions';
import {
  cleanupLocations,
  cleanupLocationLaborTargets,
  // cleanupManagerAssignmentHistory, // REMOVED: ManagerAssignmentHistory removed from schema
  cleanupHierarchyChangeLog,
  cleanupOrgUnits,
  cleanupRoles,
  cleanupEmployees,
  cleanupLocationEmployees,
  cleanupOrgUnitEmployees,
  cleanupAllTables
} from 'utils/storeMigration/cleanupAllTables';
import DashboardLayout from 'layouts/DashboardLayout'
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';

// MUI Components
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Divider,
  Chip,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  LinearProgress
} from '@mui/material';

const StoreMigrationManager = () => {
  const [loading, setLoading] = useState(false);
  const [legacyStores, setLegacyStores] = useState([]);
  const [existingLocations, setExistingLocations] = useState([]);
  const [existingLaborTargets, setExistingLaborTargets] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedStoreData, setSelectedStoreData] = useState(null);
  const [matchedLocations, setMatchedLocations] = useState([]);
  const [unmatchedLegacyStores, setUnmatchedLegacyStores] = useState([]);
  const [unmatchedLocations, setUnmatchedLocations] = useState([]);
  const [error, setError] = useState(null);
  const [clientId] = useState('grm');
  const [activeStep, setActiveStep] = useState(0); // Start directly on migration page
  const [orgUnits, setOrgUnits] = useState([]);
  const [matchedOrgUnits, setMatchedOrgUnits] = useState([]);
  const [unmatchedOrgUnits, setUnmatchedOrgUnits] = useState([]);
  const [creatingOrgUnits, setCreatingOrgUnits] = useState(false);
  const [orgUnitCreationResults, setOrgUnitCreationResults] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  const [migrationProgress, setMigrationProgress] = useState('');
  const [cleaningManagers, setCleaningManagers] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState('');
  const [individualMigrationStatus, setIndividualMigrationStatus] = useState({
    locations: { running: false, completed: false, error: null, results: null },
    laborTargets: { running: false, completed: false, error: null, results: null },
    history: { running: false, completed: false, error: null, results: null },
    metadataCleanup: { running: false, completed: false, error: null, results: null },
    roles: { running: false, completed: false, error: null, results: null },
    employees: { running: false, completed: false, error: null, results: null },
    locationEmployees: { running: false, completed: false, error: null, results: null },
    orgUnitEmployees: { running: false, completed: false, error: null, results: null }
  });
  const [cleanupStatus, setCleanupStatus] = useState({
    locations: { running: false, completed: false, error: null },
    laborTargets: { running: false, completed: false, error: null },
    hierarchyChangeLog: { running: false, completed: false, error: null },
    orgUnits: { running: false, completed: false, error: null },
    roles: { running: false, completed: false, error: null },
    employees: { running: false, completed: false, error: null },
    locationEmployees: { running: false, completed: false, error: null },
    orgUnitEmployees: { running: false, completed: false, error: null }
  });
  const [migrationCounts, setMigrationCounts] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(false); // eslint-disable-line no-unused-vars
  const [employeeMap, setEmployeeMap] = useState(new Map()); // Maps userId to employeeId
  const [rolesExist, setRolesExist] = useState(false); // Track if roles exist in DB
  const [employeeMigrationDetails, setEmployeeMigrationDetails] = useState(null); // Detailed migration progress
  const [validatingEmployees, setValidatingEmployees] = useState(false); // For validation button
  const [validationResults, setValidationResults] = useState(null); // Validation results

  // Load data based on active step
  useEffect(() => {
    if (activeStep === 0) {
      // Load all data for the migration page
      loadAllData();
      loadOrgUnitData();
      loadMigrationCounts();
      loadExistingEmployeeMap();
      checkRolesInDatabase(); // Check if roles exist
    }
  }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch legacy area/store data
      console.log('Fetching areas with listAreas2...');
      
      // Use specific parameters for store-admin report
      const areasResult = await API.graphql(
        graphqlOperation(listAreas2, {
          report: 'store-admin',
          role: 'Admin',
          user: 'grmAdmin'
        })
      );
      
      console.log('Areas result:', areasResult);
      const allAreas = areasResult.data?.listAreas2 || [];
      
      // Flatten stores from all areas including Ghost Kitchen
      const allLegacyStores = [];
      allAreas.forEach(area => {
        area.stores.forEach(store => {
          allLegacyStores.push({
            ...store,
            areaId: area.areaId,
            areaName: area.areaName,
            areaType: area.areaType
          });
        });
      });

      // Fetch existing Location entries
      console.log('Fetching Locations with listLocations...');
      console.log('Client ID:', clientId);
      
      const locationsResult = await API.graphql(
        graphqlOperation(listLocations, {
          filter: {
            clientId: {
              eq: clientId
            }
          },
          limit: 1000 // Adjust if needed
        })
      );
      
      console.log('Locations result:', locationsResult);
      const existingLocs = locationsResult.data?.listLocations?.items || [];
      console.log(`Found ${existingLocs.length} Locations`);

      // Also fetch LocationLaborTargets
      const laborTargetsResult = await API.graphql(
        graphqlOperation(listLocationLaborTargets, {
          filter: {
            clientId: {
              eq: clientId
            }
          },
          limit: 1000
        })
      );
      
      const laborTargets = laborTargetsResult.data?.listLocationLaborTargets?.items || [];
      console.log(`Found ${laborTargets.length} LocationLaborTargets`);

      setLegacyStores(allLegacyStores);
      setExistingLocations(existingLocs);
      setExistingLaborTargets(laborTargets);
      
      // Compare and create mapping
      compareAndMatchLocations(allLegacyStores, existingLocs);
      
    } catch (err) {
      const errorMessage = err?.message || err?.errors?.[0]?.message || JSON.stringify(err);
      setError(`Failed to load data: ${errorMessage}`);
      console.error('Load error:', err);
      console.error('Error details:', {
        message: err?.message,
        errors: err?.errors,
        stack: err?.stack,
        fullError: err
      });
    } finally {
      setLoading(false);
    }
  };

  // Load OrgUnit data when moving to step 2
  const loadOrgUnitData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch legacy areas (already loaded)
      const areasResult = await API.graphql(
        graphqlOperation(listAreas2, {
          report: 'store-admin',
          role: 'Admin',
          user: 'grmAdmin'
        })
      );
      
      const allAreas = areasResult.data?.listAreas2 || [];
      
      // Fetch existing OrgUnit entries
      console.log('Fetching OrgUnits...');
      const orgUnitsResult = await API.graphql(
        graphqlOperation(listOrgUnits, {
          filter: {
            clientId: {
              eq: clientId
            }
          },
          limit: 1000
        })
      );
      
      const existingOrgUnits = orgUnitsResult.data?.listOrgUnits?.items || [];
      console.log(`Found ${existingOrgUnits.length} OrgUnits`);
      
      setOrgUnits(existingOrgUnits);
      
      // Compare areas with OrgUnits
      compareAndMatchOrgUnits(allAreas, existingOrgUnits);
      
    } catch (err) {
      const errorMessage = err?.message || err?.errors?.[0]?.message || JSON.stringify(err);
      setError(`Failed to load OrgUnit data: ${errorMessage}`);
      console.error('Load OrgUnit error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Compare areas with OrgUnits
  const compareAndMatchOrgUnits = (areas, orgUnits) => {
    console.log('Starting OrgUnit comparison...');
    console.log('Areas:', areas.length);
    console.log('OrgUnits:', orgUnits.length);
    console.log('Sample area data:', areas.length > 0 ? JSON.stringify(areas[0], null, 2) : 'No areas');
    
    // Create maps for matching
    const orgUnitsByName = new Map(orgUnits.filter(o => o.name).map(o => [o.name.toLowerCase(), o]));
    const orgUnitsById = new Map(orgUnits.map(o => [o.id, o]));
    
    const matched = [];
    const unmatched = [];
    const matchedOrgUnitIds = new Set();
    
    areas.forEach(area => {
      let matchedOrgUnit = null;
      let matchType = null;
      
      // Try to match by area name
      if (area.areaName && orgUnitsByName.has(area.areaName.toLowerCase())) {
        matchedOrgUnit = orgUnitsByName.get(area.areaName.toLowerCase());
        matchType = 'name';
      }
      // Try to match by areaId
      else if (area.areaId && orgUnitsById.has(area.areaId)) {
        matchedOrgUnit = orgUnitsById.get(area.areaId);
        matchType = 'id';
      }
      
      if (matchedOrgUnit) {
        matched.push({
          area,
          orgUnit: matchedOrgUnit,
          matchType
        });
        matchedOrgUnitIds.add(matchedOrgUnit.id);
      } else {
        // Add area with all required fields for OrgUnit creation
        unmatched.push({
          areaId: area.areaId,
          areaName: area.areaName,
          areaManager: area.areaManager || null,
          areaType: area.areaType || 'Area',
          storeCount: area.stores?.length || 0,
          stores: area.stores || []
        });
      }
    });
    
    // Find unmatched OrgUnits
    const unmatchedOrgUnits = orgUnits.filter(org => !matchedOrgUnitIds.has(org.id));
    
    setMatchedOrgUnits(matched);
    setUnmatchedOrgUnits(unmatched);
    
    console.log(`Matched ${matched.length} OrgUnits`);
    console.log(`Unmatched areas: ${unmatched.length}`, unmatched.map(a => a.areaName));
    console.log(`Unmatched OrgUnits: ${unmatchedOrgUnits.length}`, unmatchedOrgUnits.map(o => o.name));
  };

  const compareAndMatchLocations = (legacy, existing) => {
    console.log('Starting location comparison...');
    console.log('Legacy stores sample:', legacy.slice(0, 3));
    console.log('Existing Locations sample:', existing.slice(0, 3));
    
    // Create maps for matching - Locations use locationName
    const existingByLocationName = new Map(existing.filter(s => s.locationName).map(s => [s.locationName, s]));
    
    // Build maps for external systems (Toast ID matching)
    const locationsWithToastId = [];
    existing.forEach(loc => {
      if (loc.externalSystems && Array.isArray(loc.externalSystems)) {
        const toastSystem = loc.externalSystems.find(sys => sys.systemType === 'TOAST_POS');
        if (toastSystem?.systemId) {
          locationsWithToastId.push({ location: loc, toastId: toastSystem.systemId });
        }
      }
    });
    
    console.log(`Building toastId map: ${locationsWithToastId.length} Locations have Toast POS out of ${existing.length} total`);
    const existingByToastId = new Map(locationsWithToastId.map(item => [item.toastId, item.location]));
    
    const matched = [];
    const unmatchedLegacy = [];
    const matchedLocationIds = new Set();
    
    legacy.forEach(legacyStore => {
      // Try multiple matching strategies in priority order
      let matchedLocation = null;
      let matchType = null;
      
      // FIRST PRIORITY: Try by shortName matching locationName
      if (existingByLocationName.has(legacyStore.shortName)) {
        matchedLocation = existingByLocationName.get(legacyStore.shortName);
        matchType = 'shortName->locationName';
      }
      
      // SECOND PRIORITY: Try by toastId match via externalSystems
      if (!matchedLocation && legacyStore.toastId) {
        console.log(`Checking toastId for ${legacyStore.shortName}: ${legacyStore.toastId}`);
        
        if (existingByToastId.has(legacyStore.toastId)) {
          matchedLocation = existingByToastId.get(legacyStore.toastId);
          matchType = 'toastId';
          console.log(`  ✓ Found toastId match for ${legacyStore.shortName}!`);
        } else {
          console.log(`  ✗ No toastId match found for ${legacyStore.shortName}`);
        }
      }
      
      // THIRD PRIORITY: Check if longName matches any Aloha POS system
      if (!matchedLocation && legacyStore.longName) {
        for (const loc of existing) {
          if (loc.externalSystems && Array.isArray(loc.externalSystems)) {
            const alohaSystem = loc.externalSystems.find(sys => 
              sys.systemType === 'ALOHA_POS' && sys.systemId === legacyStore.longName
            );
            if (alohaSystem) {
              matchedLocation = loc;
              matchType = 'longName->AlohaPOS';
              break;
            }
          }
        }
      }
      
      if (matchedLocation) {
        // Find associated labor targets
        const laborTarget = existingLaborTargets.find(lt => lt.locationId === matchedLocation.id);
        
        matched.push({
          legacyStore,
          location: matchedLocation,
          laborTarget: laborTarget || null,
          matchType
        });
        matchedLocationIds.add(matchedLocation.id);
      } else {
        unmatchedLegacy.push(legacyStore);
      }
    });
    
    // Find unmatched Locations
    const unmatchedLocs = existing.filter(loc => !matchedLocationIds.has(loc.id));
    
    setMatchedLocations(matched);
    setUnmatchedLegacyStores(unmatchedLegacy);
    setUnmatchedLocations(unmatchedLocs);
    
    console.log(`Matched ${matched.length} locations`);
    console.log(`Unmatched legacy stores: ${unmatchedLegacy.length}`, unmatchedLegacy.map(s => `${s.shortName} (${s.longName || 'no longName'})`));
    console.log(`Unmatched Locations: ${unmatchedLocs.length}`, unmatchedLocs.map(s => s.locationName || s.id));
    
    const matchBreakdown = matched.reduce((acc, m) => {
      acc[m.matchType] = (acc[m.matchType] || 0) + 1;
      return acc;
    }, {});
    console.log('Match type breakdown:', matchBreakdown);
    
    // Log specific matches for the new substring strategy
    const substringMatches = matched.filter(m => m.matchType === 'longName contains storeName');
    if (substringMatches.length > 0) {
      console.log('Substring matches (longName contains storeName):');
      substringMatches.forEach(m => {
        console.log(`  ${m.legacyStore.shortName}: "${m.legacyStore.longName}" contains "${m.storeRecord.storeName}"`);
      });
    }
    
    // Log toastId matches
    const toastIdMatches = matched.filter(m => m.matchType === 'toastId');
    if (toastIdMatches.length > 0) {
      console.log('ToastId matches:');
      toastIdMatches.forEach(m => {
        console.log(`  ${m.legacyStore.shortName} <-> ${m.storeRecord.storeName || m.storeRecord.storeNumber} (toastId: ${m.legacyStore.toastId})`);
      });
    }
  };

  // Handle store selection
  const handleStoreSelect = (storeId) => {
    setSelectedStoreId(storeId);
    
    // Find the selected store data
    const legacyStore = legacyStores.find(s => s.storeId === storeId);
    const matchedStore = matchedLocations.find(m => m.legacyStore.storeId === storeId);
    
    setSelectedStoreData({
      legacy: legacyStore,
      location: matchedStore?.location || null,
      laborTarget: matchedStore?.laborTarget || null,
      matchType: matchedStore?.matchType || null
    });
  };

  // Get sorted store list for dropdown
  const getSortedStores = () => {
    return [...legacyStores].sort((a, b) => 
      a.shortName.localeCompare(b.shortName)
    );
  };

  // Render helpers
  const renderDataField = (fieldName, value) => {
    // Handle different types of values
    let displayValue = 'No Value';
    
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value === 'object') {
        // For objects, stringify them
        displayValue = JSON.stringify(value);
      } else {
        displayValue = String(value);
      }
    }
    
    return (
      <Box mb={1}>
        <Typography variant="body2" component="span" fontWeight="bold" color="text.secondary">
          {fieldName}:
        </Typography>
        <Typography variant="body2" component="span" ml={1}>
          {displayValue}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6">Loading store data...</Typography>
      </Box>
    );
  }

  // Navigation handlers

  // Load existing employee mappings
  const loadExistingEmployeeMap = async () => {
    try {
      console.log('Loading existing employee mappings...');
      
      // Fetch all users with employeeId
      const usersResult = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { 
            client: { eq: clientId },
            employeeId: { attributeExists: true }
          },
          limit: 1000
        })
      );
      
      const users = usersResult.data?.listUsers?.items || [];
      const newEmployeeMap = new Map();
      
      users.forEach(user => {
        if (user.employeeId) {
          newEmployeeMap.set(user.id, user.employeeId);
        }
      });
      
      setEmployeeMap(newEmployeeMap);
      console.log(`Loaded ${newEmployeeMap.size} existing employee mappings`);
      
    } catch (error) {
      console.error('Error loading existing employee map:', error);
    }
  };

  // Check if required roles exist in database
  const checkRolesInDatabase = async () => {
    try {
      console.log('Checking for existing roles in database...');
      const rolesCheck = await checkRequiredRolesExist(clientId);
      
      if (rolesCheck.allExist) {
        console.log('✅ All required roles exist in database');
        setRolesExist(true);
        // Also mark roles as completed in migration status
        setIndividualMigrationStatus(prev => ({
          ...prev,
          roles: {
            ...prev.roles,
            completed: true,
            results: {
              created: rolesCheck.totalExisting,
              skipped: 0,
              errors: []
            }
          }
        }));
      } else {
        console.log(`⚠️ Missing roles: ${rolesCheck.missingRoles?.join(', ')}`);
        setRolesExist(false);
      }
      
    } catch (error) {
      console.error('Error checking roles:', error);
      setRolesExist(false);
    }
  };

  // Load migration counts
  const loadMigrationCounts = async () => {
    setLoadingCounts(true);
    try {
      const result = await getMigrationCounts('grm');
      if (result.success) {
        setMigrationCounts(result.counts);
        
        // Check if employees have already been created (all or most)
        // If existing employees equals or is close to total users, mark as completed
        if (result.counts.employees) {
          const { total, existing } = result.counts.employees;
          if (existing > 0 && existing >= total * 0.9) { // 90% threshold for completion
            setIndividualMigrationStatus(prev => ({
              ...prev,
              employees: { 
                ...prev.employees, 
                completed: true,
                results: {
                  created: existing,
                  linked: existing,
                  skipped: total - existing
                }
              }
            }));
            console.log(`Employees already migrated: ${existing}/${total}`);
          }
        }
        
        // Check if locations have already been migrated
        if (result.counts.locations) {
          const { total, existing } = result.counts.locations;
          if (existing > 0 && existing >= total * 0.9) {
            setIndividualMigrationStatus(prev => ({
              ...prev,
              locations: { 
                ...prev.locations, 
                completed: true,
                results: {
                  created: existing,
                  updated: 0,
                  skipped: total - existing
                }
              }
            }));
            console.log(`Locations already migrated: ${existing}/${total}`);
          }
        }
        
        // Check if labor targets have already been migrated
        if (result.counts.laborTargets) {
          const { existing, totalLocations } = result.counts.laborTargets;
          if (existing > 0 && existing >= totalLocations * 0.9) {
            setIndividualMigrationStatus(prev => ({
              ...prev,
              laborTargets: { 
                ...prev.laborTargets, 
                completed: true,
                results: {
                  created: existing,
                  updated: 0,
                  skipped: 0
                }
              }
            }));
            console.log(`Labor targets already migrated: ${existing}/${totalLocations}`);
          }
        }
        
        // Check if OrgUnits have already been created
        if (result.counts.orgUnits) {
          const { total, existing } = result.counts.orgUnits;
          if (existing > 0 && existing >= total * 0.9) {
            setOrgUnitCreationResults({
              created: existing,
              updated: 0,
              skipped: total - existing
            });
            console.log(`OrgUnits already created: ${existing}/${total}`);
          }
        }
        
        // Check if LocationEmployees have already been created
        if (result.counts.locationEmployees) {
          const { total, existing } = result.counts.locationEmployees;
          if (existing > 0) {
            setIndividualMigrationStatus(prev => ({
              ...prev,
              locationEmployees: { 
                ...prev.locationEmployees, 
                completed: existing >= total * 0.5, // Lower threshold as not all GMs may exist
                results: {
                  created: existing,
                  skipped: total - existing
                }
              }
            }));
            console.log(`LocationEmployees already created: ${existing}/${total}`);
          }
        }
        
        // Check if OrgUnitEmployees have already been created
        if (result.counts.orgUnitEmployees) {
          const { total, existing } = result.counts.orgUnitEmployees;
          if (existing > 0) {
            setIndividualMigrationStatus(prev => ({
              ...prev,
              orgUnitEmployees: { 
                ...prev.orgUnitEmployees, 
                completed: existing >= total * 0.5, // Lower threshold as not all managers may exist
                results: {
                  created: existing,
                  skipped: total - existing
                }
              }
            }));
            console.log(`OrgUnitEmployees already created: ${existing}/${total}`);
          }
        }
      } else {
        console.error('Failed to load migration counts:', result.error);
      }
    } catch (error) {
      console.error('Error loading migration counts:', error);
    } finally {
      setLoadingCounts(false);
    }
  };


  // Handle creating missing OrgUnits
  const handleCreateMissingOrgUnits = async () => {
    setCreatingOrgUnits(true);
    setError(null);
    
    try {
      console.log('=== Starting OrgUnit Creation Process ===');
      console.log('Client ID:', clientId);
      console.log('Number of unmatched OrgUnits to create:', unmatchedOrgUnits.length);
      console.log('Unmatched OrgUnits data:', JSON.stringify(unmatchedOrgUnits, null, 2));
      
      const results = await createMissingOrgUnits(unmatchedOrgUnits, clientId);
      
      console.log('=== Creation Results ===');
      console.log('Results object:', JSON.stringify(results, null, 2));
      console.log(`Successfully created: ${results.created.length}`);
      console.log(`Failed to create: ${results.failed.length}`);
      
      if (results.failed.length > 0) {
        console.error('Failed OrgUnits details:');
        results.failed.forEach((failure, index) => {
          console.error(`${index + 1}. Area: ${failure.areaName} (ID: ${failure.areaId})`);
          console.error(`   Error: ${failure.error}`);
        });
      }
      
      setOrgUnitCreationResults(results);
      
      // If all were created successfully, reload the OrgUnit data
      if (results.failed.length === 0) {
        console.log('All OrgUnits created successfully, reloading data...');
        await loadOrgUnitData();
      }
      
      // Show success message
      if (results.created.length > 0) {
        console.log(`✅ Successfully created ${results.created.length} OrgUnits`);
      }
      
    } catch (err) {
      const errorMessage = err?.message || 'Failed to create OrgUnits';
      setError(errorMessage);
      console.error('=== Create OrgUnits Error ===');
      console.error('Error message:', err?.message);
      console.error('Error stack:', err?.stack);
      console.error('Full error object:', err);
    } finally {
      setCreatingOrgUnits(false);
    }
  };

  // Handle metadata cleanup - DISABLED for Location migration
  const handleMetadataCleanup = async () => {
    // This function was for StoreRecord cleanup - no longer needed for Location migration
    alert('Metadata cleanup is disabled. This feature was for StoreRecord maintenance.');
    return;
    
    // Original StoreRecord cleanup code commented out:
    /*
    if (!window.confirm('This will reset all metadata in StoreRecords and add test goLiveDate values. Are you sure?')) {
      return;
    }
    
    setMigrating(true);
    setError(null);
    setMigrationProgress('Cleaning metadata...');
    
    try {
      // Get all store records
      const storesResult = await API.graphql(
        graphqlOperation(storesByClientSorted, { clientId })
      );
      const stores = storesResult.data?.storesByClientSorted?.items || [];
      
      let updated = 0;
      let errors = 0;
      
      // Generate random date between 2020 and 2023
      const getRandomGoLiveDate = () => {
        const start = new Date('2020-01-01');
        const end = new Date('2023-12-31');
        const randomTime = start.getTime() + Math.random() * (end.getTime() - start.getTime());
        return new Date(randomTime).toISOString().split('T')[0];
      };
      
      for (const store of stores) {
        try {
          setMigrationProgress(`Cleaning metadata for ${store.storeName} (${updated + 1}/${stores.length})...`);
          
          // Create clean metadata with only goLiveDate
          const cleanMetadata = {
            toast: {
              goLiveDate: getRandomGoLiveDate()
            }
          };
          
          await API.graphql(
            graphqlOperation(updateStoreRecord, {
              input: {
                id: store.id,
                metadata: JSON.stringify(cleanMetadata)
              }
            })
          );
          
          updated++;
        } catch (err) {
          console.error(`Failed to clean metadata for ${store.storeName}:`, err);
          errors++;
        }
      }
      
      setMigrationProgress(`Metadata cleanup complete: ${updated} updated, ${errors} errors`);
      
      // Refresh the stores data
      await loadAllData();
      
    } catch (err) {
      const errorMessage = err?.message || err?.errors?.[0]?.message || JSON.stringify(err);
      setError(`Metadata cleanup failed: ${errorMessage}`);
      console.error('Metadata cleanup error:', err);
    } finally {
      setMigrating(false);
    }
    */
  };

  // Handle starting the migration
  const handleStartMigration = async () => {
    setMigrating(true);
    setError(null);
    setMigrationProgress('Initializing Location migration...');
    
    try {
      console.log('=== Starting Location Migration Process ===');
      console.log('Client ID:', clientId);
      
      // Use the new Location migration preset
      setMigrationProgress('Creating Location records and related data...');
      const result = await migrationPresets.location(clientId);
      
      console.log('=== Migration Complete ===');
      console.log('Result:', result);
      
      if (result.success) {
        setMigrationProgress('Location migration completed successfully!');
        setMigrationResults(result);
        
        // Show success details
        if (result.migrationResults) {
          const { locations, laborTargets, managers, history, orgUnits } = result.migrationResults;
          console.log(`✅ Migration Summary:`);
          console.log(`   - Locations created: ${locations.created}`);
          console.log(`   - Labor targets created: ${laborTargets.created}`);
          console.log(`   - Manager assignments: ${managers.created}`);
          console.log(`   - History records: ${history.created}`);
          console.log(`   - OrgUnits created: ${orgUnits.created}`);
          
          const totalErrors = locations.errors.length + laborTargets.errors.length + 
                            managers.errors.length + history.errors.length + orgUnits.errors.length;
          if (totalErrors > 0) {
            console.warn(`   - Total errors: ${totalErrors}`);
          }
        }
        
        // Reload data to show the new Locations
        await loadAllData();
      } else {
        setMigrationProgress('Migration failed');
        setError(result.error || 'Migration failed for unknown reason');
        console.error('Migration failed:', result.error);
      }
      
    } catch (err) {
      const errorMessage = err?.message || 'Failed to run migration';
      setError(errorMessage);
      setMigrationProgress('Migration failed with error');
      console.error('=== Migration Error ===');
      console.error('Error:', err);
    } finally {
      setMigrating(false);
    }
  };

  // Individual migration handlers
  const handleMigrateLocationsOnly = async () => {
    setIndividualMigrationStatus(prev => ({
      ...prev,
      locations: { ...prev.locations, running: true, error: null }
    }));
    
    try {
      const results = await migrateLocationsOnly(clientId);
      
      if (!results.success) {
        // Migration failed - stop and show error
        setIndividualMigrationStatus(prev => ({
          ...prev,
          locations: { 
            running: false, 
            completed: false, 
            error: results.error || 'Migration failed', 
            results 
          }
        }));
        setError(results.error || 'Location migration failed. Check console for details.');
        return;
      }
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        locations: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
      // Reload counts
      await loadMigrationCounts();
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        locations: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
    }
  };

  const handleMigrateLaborTargetsOnly = async () => {
    setIndividualMigrationStatus(prev => ({
      ...prev,
      laborTargets: { ...prev.laborTargets, running: true, error: null }
    }));
    
    try {
      const results = await migrateLaborTargetsOnly(clientId);
      
      if (!results.success) {
        // Migration failed - stop and show error
        setIndividualMigrationStatus(prev => ({
          ...prev,
          laborTargets: { 
            running: false, 
            completed: false, 
            error: results.error || 'Migration failed', 
            results 
          }
        }));
        setError(results.error || 'Labor targets migration failed. Check console for details.');
        return;
      }
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        laborTargets: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
      // Reload counts
      await loadMigrationCounts();
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        laborTargets: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
    }
  };

  const handleMigrateHistoryOnly = async () => {
    setIndividualMigrationStatus(prev => ({
      ...prev,
      history: { ...prev.history, running: true, error: null }
    }));
    
    try {
      const results = await migrateHistoryOnly(clientId);
      
      if (!results.success) {
        // Migration failed - stop and show error
        setIndividualMigrationStatus(prev => ({
          ...prev,
          history: { 
            running: false, 
            completed: false, 
            error: results.error || 'Migration failed', 
            results 
          }
        }));
        setError(results.error || 'Assignment history migration failed. Check console for details.');
        return;
      }
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        history: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
      // Reload counts
      await loadMigrationCounts();
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        history: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
    }
  };

  const handleCleanMetadata = async () => {
    if (!window.confirm('This will clean __typename from Location metadata while preserving toast data. Continue?')) {
      return;
    }
    
    setIndividualMigrationStatus(prev => ({
      ...prev,
      metadataCleanup: { ...prev.metadataCleanup, running: true, error: null }
    }));
    
    try {
      const results = await cleanLocationMetadata(clientId);
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        metadataCleanup: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
      // Reload counts
      await loadMigrationCounts();
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        metadataCleanup: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
    }
  };

  // Role creation handlers
  const handleCreateRoles = async () => {
    setIndividualMigrationStatus(prev => ({
      ...prev,
      roles: { ...prev.roles, running: true, error: null }
    }));
    
    try {
      const results = await createRoleDefinitions(clientId, (progress) => {
        console.log('Role creation progress:', progress);
      });
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        roles: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
      console.log('Role creation complete:', results);
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        roles: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
    }
  };

  const handleCleanupRoles = async () => {
    if (!window.confirm('Are you sure you want to delete ALL Role definitions? This cannot be undone!')) {
      return;
    }
    
    setCleanupStatus(prev => ({
      ...prev,
      roles: { running: true, completed: false, error: null }
    }));
    
    try {
      const results = await cleanupRoles(clientId);
      setCleanupStatus(prev => ({
        ...prev,
        roles: { running: false, completed: true, error: null }
      }));
      console.log('Role cleanup results:', results);
      
      // Reset role migration status
      setIndividualMigrationStatus(prev => ({
        ...prev,
        roles: { running: false, completed: false, error: null, results: null }
      }));
    } catch (error) {
      setCleanupStatus(prev => ({
        ...prev,
        roles: { running: false, completed: false, error: error.message }
      }));
    }
  };

  // Employee migration handlers
  const handleMigrateEmployees = async (forceOverwrite = false) => {
    setIndividualMigrationStatus(prev => ({
      ...prev,
      employees: { ...prev.employees, running: true, error: null }
    }));
    setEmployeeMigrationDetails(null); // Reset details
    
    try {
      const results = await migrateUsersToEmployees(
        clientId, 
        (progress) => {
          console.log('Employee migration progress:', progress);
          
          // Update UI with detailed progress
          if (progress.phase === 'processing' || progress.phase === 'created' || 
              progress.phase === 'skipped' || progress.phase === 'error') {
            setEmployeeMigrationDetails(prev => ({
              ...prev,
              currentUser: progress.current,
              totalUsers: progress.total,
              currentUserName: progress.userName,
              lastMessage: progress.message,
              phase: progress.phase
            }));
          }
          
          if (progress.phase === 'complete') {
            setEmployeeMigrationDetails({
              complete: true,
              summary: progress.summary,
              lastMessage: progress.message
            });
          }
        },
        { forceOverwrite } // Pass the overwrite option
      );
      
      // Store the employee map for use in subsequent migrations
      setEmployeeMap(results.employeeMap);
      
      // Store detailed results for display
      if (results.userDetails && results.userDetails.length > 0) {
        console.log('=== Employee Migration Details ===');
        console.log(`Total Users Processed: ${results.totalUsers}`);
        results.userDetails.forEach(detail => {
          const icon = detail.status === 'completed' ? '✅' : 
                       detail.status === 'skipped' ? '⏭️' : 
                       detail.status === 'error' ? '❌' : '❓';
          console.log(`${icon} [${detail.number}/${results.totalUsers}] ${detail.userName}: ${detail.message}`);
        });
        console.log('=== Summary ===');
        console.log(`Created: ${results.created}, Linked: ${results.linked}, Skipped: ${results.skipped}, Errors: ${results.errors.length}`);
      }
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        employees: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
      // Reload counts after migration
      await loadMigrationCounts();
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        employees: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
      setEmployeeMigrationDetails({
        complete: true,
        error: error.message,
        lastMessage: `Fatal error: ${error.message}`
      });
    }
  };

  const handleMigrateLocationEmployees = async () => {
    setIndividualMigrationStatus(prev => ({
      ...prev,
      locationEmployees: { ...prev.locationEmployees, running: true, error: null }
    }));
    
    try {
      const results = await migrateLocationManagers(clientId, employeeMap, (progress) => {
        console.log('Location employee migration progress:', progress);
      });
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        locationEmployees: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        locationEmployees: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
    }
  };

  // Validate User-Employee migration
  const handleValidateEmployees = async () => {
    setValidatingEmployees(true);
    setValidationResults(null);
    
    try {
      console.log('Starting User-Employee validation...');
      const results = await validateUserEmployeeMigration(clientId);
      setValidationResults(results);
      
      // Update UI based on validation results
      if (!results.valid) {
        setError(`Validation failed: ${results.usersWithMultipleEmployees.length} users with duplicates, ${results.usersWithoutEmployee.length} users without employees. Check console for details.`);
      }
      
    } catch (error) {
      console.error('Validation error:', error);
      setError(`Validation failed: ${error.message}`);
    } finally {
      setValidatingEmployees(false);
    }
  };

  const handleMigrateOrgUnitEmployees = async () => {
    setIndividualMigrationStatus(prev => ({
      ...prev,
      orgUnitEmployees: { ...prev.orgUnitEmployees, running: true, error: null }
    }));
    
    try {
      const results = await migrateOrgUnitManagers(clientId, employeeMap, (progress) => {
        console.log('OrgUnit employee migration progress:', progress);
      });
      
      setIndividualMigrationStatus(prev => ({
        ...prev,
        orgUnitEmployees: { 
          running: false, 
          completed: true, 
          error: null, 
          results 
        }
      }));
      
    } catch (error) {
      setIndividualMigrationStatus(prev => ({
        ...prev,
        orgUnitEmployees: { 
          running: false, 
          completed: false, 
          error: error.message,
          results: null
        }
      }));
    }
  };

  // Handle manager assignment cleanup
  const handleManagerCleanup = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL ManagerAssignment records for this client. This cannot be undone. Are you sure?')) {
      return;
    }
    
    setCleaningManagers(true);
    setError(null);
    setCleanupProgress('Starting cleanup...');
    
    try {
      const results = await cleanupAllManagerAssignments(clientId, {
        onProgress: (progress) => {
          setCleanupProgress(progress.message);
        }
      });
      
      setCleanupProgress(`Cleanup complete! Deleted ${results.deleted} of ${results.totalFound} records.`);
      
      if (results.errors.length > 0) {
        setError(`Cleanup completed with ${results.errors.length} errors. Check console for details.`);
      }
      
      // Show success alert
      setTimeout(() => {
        alert(`✅ Manager cleanup complete!\n\nDeleted: ${results.deleted} records\nErrors: ${results.errors.length}`);
        setCleanupProgress('');
      }, 1000);
      
    } catch (err) {
      const errorMessage = err?.message || 'Failed to clean up manager assignments';
      setError(errorMessage);
      setCleanupProgress('Cleanup failed');
      console.error('Manager cleanup error:', err);
    } finally {
      setCleaningManagers(false);
    }
  };

  // Render migration step
  const renderMigrationStep = () => {
    // Get unique areas for OrgUnit summary
    const uniqueAreas = [];
    const areaIds = new Set();
    
    legacyStores.forEach(store => {
      if (!areaIds.has(store.areaId)) {
        areaIds.add(store.areaId);
        uniqueAreas.push({
          areaId: store.areaId,
          areaName: store.areaName,
          areaType: store.areaType
        });
      }
    });

    return (
      <>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              Store Migration Manager
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Migrate your store data from MongoDB to AWS Location hierarchy.
            </Typography>
          </CardContent>
        </Card>

        {/* Data Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Data Summary</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3">{uniqueAreas.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Areas
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3">{legacyStores.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Stores
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main">
                    {matchedOrgUnits.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Existing OrgUnits
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="warning.main">
                    {unmatchedOrgUnits.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    OrgUnits to Create
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Individual Migration Steps */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Migration Steps</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Execute these steps in order or run the complete migration at once.
            </Typography>
            
            <Grid container spacing={2}>
              {/* OrgUnit Creation - First Step */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: unmatchedOrgUnits.length > 0 ? 'warning.light' : 'grey.100' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    1. Create OrgUnits
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create missing OrgUnits from areas before migrating locations
                  </Typography>
                  {migrationCounts?.orgUnits && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Total: {migrationCounts.orgUnits.total} | 
                        Existing: {migrationCounts.orgUnits.existing} | 
                        To Create: {migrationCounts.orgUnits.toCreate}
                      </Typography>
                    </Box>
                  )}
                  {!migrationCounts?.orgUnits && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Areas: {uniqueAreas.length} | 
                        Existing: {matchedOrgUnits.length} | 
                        To Create: {unmatchedOrgUnits.length}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    {unmatchedOrgUnits.length > 0 || migrationCounts?.orgUnits?.toCreate > 0 ? (
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={handleCreateMissingOrgUnits}
                        disabled={creatingOrgUnits || cleanupStatus.orgUnits?.running}
                        sx={{ flex: 1 }}
                        startIcon={creatingOrgUnits ? <CircularProgress size={16} /> : null}
                      >
                        {creatingOrgUnits ? 'Creating...' : `Create ${unmatchedOrgUnits.length || migrationCounts?.orgUnits?.toCreate || 0} OrgUnits`}
                      </Button>
                    ) : (
                      <Alert severity="success" sx={{ py: 0.5, flex: 1 }}>
                        <Typography variant="caption">All OrgUnits exist</Typography>
                      </Alert>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCleanupOrgUnits}
                      disabled={creatingOrgUnits || cleanupStatus.orgUnits?.running}
                      sx={{ minWidth: '80px' }}
                    >
                      {cleanupStatus.orgUnits?.running ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Clear'
                      )}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              
              {/* Migrate Locations */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    2. Migrate Locations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create Location records from store data
                  </Typography>
                  {migrationCounts?.locations && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Total: {migrationCounts.locations.total} | 
                        Existing: {migrationCounts.locations.existing} | 
                        To Create: {migrationCounts.locations.toCreate}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleMigrateLocationsOnly}
                      disabled={migrating || individualMigrationStatus.locations?.running || cleanupStatus.locations?.running}
                      sx={{ flex: 1 }}
                    >
                      {individualMigrationStatus.locations?.running ? (
                        <CircularProgress size={20} />
                      ) : individualMigrationStatus.locations?.completed ? (
                        'Completed ✓'
                      ) : (
                        'Migrate'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCleanupLocations}
                      disabled={migrating || cleanupStatus.locations?.running || individualMigrationStatus.locations?.running}
                      sx={{ minWidth: '80px' }}
                    >
                      {cleanupStatus.locations?.running ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Clear'
                      )}
                    </Button>
                  </Box>
                  {individualMigrationStatus.locations?.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {individualMigrationStatus.locations.error}
                    </Alert>
                  )}
                  {individualMigrationStatus.locations?.results && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Created: {individualMigrationStatus.locations.results.counts?.created || individualMigrationStatus.locations.results.created} |
                        With StoreRecord ID: {individualMigrationStatus.locations.results.counts?.withStoreRecordId || individualMigrationStatus.locations.results.withStoreRecordId || 0} |
                        With New UUID: {individualMigrationStatus.locations.results.counts?.withGeneratedId || individualMigrationStatus.locations.results.withGeneratedId || 0}
                      </Typography>
                      {(individualMigrationStatus.locations.results.counts?.withGeneratedId || individualMigrationStatus.locations.results.withGeneratedId) > 0 && (
                        <>
                          <Typography variant="caption" display="block" color="warning.main">
                            ⚠️ {individualMigrationStatus.locations.results.counts?.withGeneratedId || individualMigrationStatus.locations.results.withGeneratedId} locations won't have MetricData
                          </Typography>
                          {individualMigrationStatus.locations.results.unmatchedStores && individualMigrationStatus.locations.results.unmatchedStores.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" display="block" color="warning.main">
                                Unmatched stores: {individualMigrationStatus.locations.results.unmatchedStores.map(s => s.shortName).join(', ')}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              {/* Migrate Labor Targets */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    3. Migrate Labor Targets
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create LocationLaborTargets with theoretical hours
                  </Typography>
                  {migrationCounts?.laborTargets && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Total: {migrationCounts.laborTargets.total} | 
                        Existing: {migrationCounts.laborTargets.existing} | 
                        To Create: {migrationCounts.laborTargets.toCreate}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleMigrateLaborTargetsOnly}
                      disabled={migrating || individualMigrationStatus.laborTargets?.running || cleanupStatus.laborTargets?.running}
                      sx={{ flex: 1 }}
                    >
                      {individualMigrationStatus.laborTargets?.running ? (
                        <CircularProgress size={20} />
                      ) : individualMigrationStatus.laborTargets?.completed ? (
                        'Completed ✓'
                      ) : (
                        'Migrate'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCleanupLaborTargets}
                      disabled={migrating || cleanupStatus.laborTargets?.running || individualMigrationStatus.laborTargets?.running}
                      sx={{ minWidth: '80px' }}
                    >
                      {cleanupStatus.laborTargets?.running ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Clear'
                      )}
                    </Button>
                  </Box>
                  {individualMigrationStatus.laborTargets?.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {individualMigrationStatus.laborTargets.error}
                    </Alert>
                  )}
                </Paper>
              </Grid>
              
              {/* Clean Metadata */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    4. Clean Metadata
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Remove __typename from Location metadata
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleCleanMetadata}
                    disabled={migrating || individualMigrationStatus.metadataCleanup?.running}
                    fullWidth
                  >
                    {individualMigrationStatus.metadataCleanup?.running ? (
                      <CircularProgress size={20} />
                    ) : individualMigrationStatus.metadataCleanup?.completed ? (
                      'Completed ✓'
                    ) : (
                      'Clean Metadata'
                    )}
                  </Button>
                  {individualMigrationStatus.metadataCleanup?.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {individualMigrationStatus.metadataCleanup.error}
                    </Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Employee Migration Section - NEW */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Employee Migration (New Schema)</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Migrate existing Users to the new Employee-based hierarchy model. Execute these steps in order.
            </Typography>
            
            <Grid container spacing={2}>
              {/* Create Role Definitions */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    1. Create Role Definitions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Generate standard role definitions for the hierarchy
                  </Typography>
                  <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" display="block">
                      <strong>Roles to create:</strong><br/>
                      ORGUNIT: AM, FAM<br/>
                      LOCATION: GM (default), FGM<br/>
                      BOTH: ADM, EX, SUP
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={handleCreateRoles}
                      disabled={migrating || individualMigrationStatus.roles?.running || cleanupStatus.roles?.running}
                      sx={{ flex: 1 }}
                      startIcon={individualMigrationStatus.roles?.running ? <CircularProgress size={16} /> : null}
                    >
                      {individualMigrationStatus.roles?.running ? (
                        'Creating...'
                      ) : individualMigrationStatus.roles?.completed ? (
                        `✓ ${individualMigrationStatus.roles?.results?.created || 0} Created`
                      ) : (
                        'Create Roles'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCleanupRoles}
                      disabled={migrating || cleanupStatus.roles?.running || individualMigrationStatus.roles?.running}
                      sx={{ minWidth: '80px' }}
                    >
                      {cleanupStatus.roles?.running ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Clear'
                      )}
                    </Button>
                  </Box>
                  {individualMigrationStatus.roles?.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {individualMigrationStatus.roles.error}
                    </Alert>
                  )}
                  {individualMigrationStatus.roles?.results && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Created: {individualMigrationStatus.roles.results.created} | 
                        Skipped: {individualMigrationStatus.roles.results.skipped}
                        {individualMigrationStatus.roles.results.errors?.length > 0 && (
                          <span> | Errors: {individualMigrationStatus.roles.results.errors.length}</span>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              {/* Migrate Users to Employees */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: (individualMigrationStatus.roles?.completed || rolesExist) ? 'info.light' : 'grey.100' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    2. Create Employee Records
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create Employee records from existing Users
                  </Typography>
                  {migrationCounts?.employees && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Total Users: {migrationCounts.employees.total} | 
                        Existing Employees: {migrationCounts.employees.existing} | 
                        To Create: {migrationCounts.employees.toCreate}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Show detailed progress during migration */}
                  {employeeMigrationDetails && !employeeMigrationDetails.complete && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block" color="primary">
                        Processing: [{employeeMigrationDetails.currentUser}/{employeeMigrationDetails.totalUsers}] {employeeMigrationDetails.currentUserName}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={(employeeMigrationDetails.currentUser / employeeMigrationDetails.totalUsers) * 100} 
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  )}
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      onClick={() => handleMigrateEmployees(false)}
                      disabled={(!individualMigrationStatus.roles?.completed && !rolesExist) || migrating || individualMigrationStatus.employees?.running || cleanupStatus.employees?.running || validatingEmployees}
                      sx={{ flex: 1 }}
                      startIcon={individualMigrationStatus.employees?.running ? <CircularProgress size={16} /> : null}
                    >
                      {individualMigrationStatus.employees?.running ? (
                        employeeMigrationDetails?.currentUser ? 
                          `[${employeeMigrationDetails.currentUser}/${employeeMigrationDetails.totalUsers}]` : 
                          'Creating...'
                      ) : individualMigrationStatus.employees?.completed ? (
                        `✓ ${individualMigrationStatus.employees?.results?.created || 0} Created`
                      ) : (
                        'Create'
                      )}
                    </Button>
                    {/* Add Overwrite button if there are skipped users */}
                    {individualMigrationStatus.employees?.results?.skipped > 0 && (
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => {
                          if (window.confirm(`This will overwrite ${individualMigrationStatus.employees.results.skipped} existing employee links. Continue?`)) {
                            handleMigrateEmployees(true);
                          }
                        }}
                        disabled={migrating || individualMigrationStatus.employees?.running || cleanupStatus.employees?.running || validatingEmployees}
                        sx={{ minWidth: '100px' }}
                      >
                        Overwrite
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCleanupEmployees}
                      disabled={migrating || cleanupStatus.employees?.running || individualMigrationStatus.employees?.running || validatingEmployees}
                      sx={{ minWidth: '80px' }}
                    >
                      {cleanupStatus.employees?.running ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Clear'
                      )}
                    </Button>
                  </Box>
                  {/* Add Validate button */}
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleValidateEmployees}
                      disabled={migrating || validatingEmployees || individualMigrationStatus.employees?.running || cleanupStatus.employees?.running}
                      fullWidth
                      startIcon={validatingEmployees ? <CircularProgress size={16} /> : null}
                    >
                      {validatingEmployees ? 'Validating...' : 'Validate Migration'}
                    </Button>
                  </Box>
                  {/* Show validation results if available */}
                  {validationResults && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: validationResults.valid ? 'success.light' : 'error.light', borderRadius: 1 }}>
                      <Typography variant="caption" display="block" color={validationResults.valid ? 'success.dark' : 'error.dark'}>
                        {validationResults.valid ? (
                          `✅ Valid: ${validationResults.totalUsers} Users = ${validationResults.totalEmployees} Employees`
                        ) : (
                          <>
                            ❌ Invalid: {validationResults.totalUsers} Users, {validationResults.totalEmployees} Employees
                            {validationResults.usersWithMultipleEmployees.length > 0 && (
                              <><br />Duplicates: {validationResults.usersWithMultipleEmployees.length} users</>
                            )}
                            {validationResults.usersWithoutEmployee.length > 0 && (
                              <><br />Missing: {validationResults.usersWithoutEmployee.length} users</>
                            )}
                          </>
                        )}
                      </Typography>
                    </Box>
                  )}
                  {(!individualMigrationStatus.roles?.completed && !rolesExist) && (
                    <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                      <Typography variant="caption">Create roles first (7 required roles not found in database)</Typography>
                    </Alert>
                  )}
                  {individualMigrationStatus.employees?.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {individualMigrationStatus.employees.error}
                    </Alert>
                  )}
                  {individualMigrationStatus.employees?.results && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        <strong>Results:</strong> Created: {individualMigrationStatus.employees.results.created} | 
                        Linked: {individualMigrationStatus.employees.results.linked} | 
                        Skipped: {individualMigrationStatus.employees.results.skipped}
                        {individualMigrationStatus.employees.results.errors?.length > 0 && (
                          <span style={{ color: 'red' }}> | Errors: {individualMigrationStatus.employees.results.errors.length}</span>
                        )}
                      </Typography>
                      {individualMigrationStatus.employees.results.summary && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Total Processed: {individualMigrationStatus.employees.results.summary.totalProcessed} users
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              {/* Migrate Location Managers */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: individualMigrationStatus.employees?.completed ? 'info.light' : 'grey.100' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    3. Location Assignments
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create LocationEmployee assignments from managers
                  </Typography>
                  {migrationCounts?.locationEmployees && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Total Locations: {migrationCounts.locationEmployees.total} | 
                        Existing Assignments: {migrationCounts.locationEmployees.existing} | 
                        To Create: {migrationCounts.locationEmployees.toCreate}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      onClick={handleMigrateLocationEmployees}
                      disabled={!individualMigrationStatus.employees?.completed || migrating || individualMigrationStatus.locationEmployees?.running || cleanupStatus.locationEmployees?.running}
                      sx={{ flex: 1 }}
                      startIcon={individualMigrationStatus.locationEmployees?.running ? <CircularProgress size={16} /> : null}
                    >
                      {individualMigrationStatus.locationEmployees?.running ? (
                        'Creating...'
                      ) : individualMigrationStatus.locationEmployees?.completed ? (
                        `✓ ${individualMigrationStatus.locationEmployees?.results?.created || 0} Created`
                      ) : (
                        'Create'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCleanupLocationEmployees}
                      disabled={migrating || cleanupStatus.locationEmployees?.running || individualMigrationStatus.locationEmployees?.running}
                      sx={{ minWidth: '80px' }}
                    >
                      {cleanupStatus.locationEmployees?.running ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Clear'
                      )}
                    </Button>
                  </Box>
                  {!individualMigrationStatus.employees?.completed && (
                    <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                      <Typography variant="caption">Create employees first</Typography>
                    </Alert>
                  )}
                  {individualMigrationStatus.locationEmployees?.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {individualMigrationStatus.locationEmployees.error}
                    </Alert>
                  )}
                  {individualMigrationStatus.locationEmployees?.results && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Created: {individualMigrationStatus.locationEmployees.results.created} | 
                        Skipped: {individualMigrationStatus.locationEmployees.results.skipped}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              {/* Migrate OrgUnit Managers */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: individualMigrationStatus.employees?.completed ? 'info.light' : 'grey.100' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    4. OrgUnit Assignments
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create OrgUnitEmployee assignments from managers
                  </Typography>
                  {migrationCounts?.orgUnitEmployees && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Total: {migrationCounts.orgUnitEmployees.total} | 
                        Existing: {migrationCounts.orgUnitEmployees.existing} | 
                        To Create: {migrationCounts.orgUnitEmployees.toCreate}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      onClick={handleMigrateOrgUnitEmployees}
                      disabled={!individualMigrationStatus.employees?.completed || migrating || individualMigrationStatus.orgUnitEmployees?.running || cleanupStatus.orgUnitEmployees?.running}
                      sx={{ flex: 1 }}
                      startIcon={individualMigrationStatus.orgUnitEmployees?.running ? <CircularProgress size={16} /> : null}
                    >
                      {individualMigrationStatus.orgUnitEmployees?.running ? (
                        'Creating...'
                      ) : individualMigrationStatus.orgUnitEmployees?.completed ? (
                        `✓ ${individualMigrationStatus.orgUnitEmployees?.results?.created || 0} Created`
                      ) : (
                        'Create'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCleanupOrgUnitEmployees}
                      disabled={migrating || cleanupStatus.orgUnitEmployees?.running || individualMigrationStatus.orgUnitEmployees?.running}
                      sx={{ minWidth: '80px' }}
                    >
                      {cleanupStatus.orgUnitEmployees?.running ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Clear'
                      )}
                    </Button>
                  </Box>
                  {!individualMigrationStatus.employees?.completed && (
                    <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                      <Typography variant="caption">Create employees first</Typography>
                    </Alert>
                  )}
                  {individualMigrationStatus.orgUnitEmployees?.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {individualMigrationStatus.orgUnitEmployees.error}
                    </Alert>
                  )}
                  {individualMigrationStatus.orgUnitEmployees?.results && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                        Created: {individualMigrationStatus.orgUnitEmployees.results.created} | 
                        Skipped: {individualMigrationStatus.orgUnitEmployees.results.skipped}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Clear All Tables Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ border: '2px solid #ff9800', borderRadius: 2, p: 2 }}>
              <Typography variant="h6" gutterBottom color="warning.main">
                ⚠️ Danger Zone - Clear All Tables
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleCleanupAllTables}
                  disabled={migrating || Object.values(cleanupStatus).some(s => s.running)}
                  startIcon={<Typography>⚠️</Typography>}
                >
                  Clear All Tables
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  This will delete ALL records from Location, LocationLaborTargets, LocationEmployee, 
                  OrgUnit, OrgUnitEmployee, Employee, and HierarchyChangeLog tables.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Migration Progress */}
        {migrating && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Migration Progress</Typography>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Running complete migration process...
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Cleanup Progress */}
        {Object.values(cleanupStatus).some(s => s.running) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Cleanup Progress</Typography>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Cleaning tables...
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Migration Results */}
        {migrationResults && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Migration Results
              </Typography>
              
              {/* Summary Statistics */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                    <Typography variant="h4">
                      {migrationResults.locations?.created || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Locations Created
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                    <Typography variant="h4">
                      {migrationResults.laborTargets?.created || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Labor Targets Created
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                    <Typography variant="h4">
                      {migrationResults.managers?.created || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Managers Assigned
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                    <Typography variant="h4">
                      {migrationResults.history?.created || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      History Records
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Detailed Results */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Details:</Typography>
                <pre style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '300px'
                }}>
                  {JSON.stringify(migrationResults, null, 2)}
                </pre>
                
                {/* Show errors if any */}
                {(migrationResults.locations?.errors?.length > 0 ||
                  migrationResults.laborTargets?.errors?.length > 0 ||
                  migrationResults.managers?.errors?.length > 0 ||
                  migrationResults.history?.errors?.length > 0) && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Errors occurred during migration. Check the details above.
                    </Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Alert severity="error">
                {error}
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" gap={2}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleStartMigration}
                  disabled={migrating || cleaningManagers}
                  startIcon={migrating ? <CircularProgress size={20} /> : null}
                >
                  {migrating ? 'Running Migration...' : 'Run Complete Migration'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </>
    );
  };

  // Cleanup handlers for individual tables
  const handleCleanupLocations = async () => {
    if (!window.confirm('Are you sure you want to delete ALL Location records? This cannot be undone!')) {
      return;
    }
    
    setCleanupStatus(prev => ({
      ...prev,
      locations: { running: true, completed: false, error: null }
    }));
    
    try {
      // Clean Location records
      const locResults = await cleanupLocations(clientId);
      
      setCleanupStatus(prev => ({
        ...prev,
        locations: { running: false, completed: true, error: null }
      }));
      console.log('Cleanup results:', { locations: locResults });
      // Refresh counts
      await loadMigrationCounts();
    } catch (error) {
      setCleanupStatus(prev => ({
        ...prev,
        locations: { running: false, completed: false, error: error.message }
      }));
    }
  };

  const handleCleanupLaborTargets = async () => {
    if (!window.confirm('Are you sure you want to delete ALL LocationLaborTargets records? This cannot be undone!')) {
      return;
    }
    
    setCleanupStatus(prev => ({
      ...prev,
      laborTargets: { running: true, completed: false, error: null }
    }));
    
    try {
      const results = await cleanupLocationLaborTargets(clientId);
      setCleanupStatus(prev => ({
        ...prev,
        laborTargets: { running: false, completed: true, error: null }
      }));
      console.log('Cleanup results:', results);
      await loadMigrationCounts();
    } catch (error) {
      setCleanupStatus(prev => ({
        ...prev,
        laborTargets: { running: false, completed: false, error: error.message }
      }));
    }
  };

  const handleCleanupOrgUnits = async () => {
    if (!window.confirm('Are you sure you want to delete ALL OrgUnit records? This cannot be undone!')) {
      return;
    }
    
    setCleanupStatus(prev => ({
      ...prev,
      orgUnits: { running: true, completed: false, error: null }
    }));
    
    try {
      const results = await cleanupOrgUnits(clientId);
      setCleanupStatus(prev => ({
        ...prev,
        orgUnits: { running: false, completed: true, error: null }
      }));
      console.log('Cleanup results:', results);
      await loadMigrationCounts();
      await loadOrgUnitData(); // Reload OrgUnit data after cleanup
    } catch (error) {
      setCleanupStatus(prev => ({
        ...prev,
        orgUnits: { running: false, completed: false, error: error.message }
      }));
    }
  };

  // Note: LocationAssignmentHistory is now cleaned with Locations

  const handleCleanupEmployees = async () => {
    if (!window.confirm('Are you sure you want to delete ALL Employee records? This cannot be undone!')) {
      return;
    }
    
    setCleanupStatus(prev => ({
      ...prev,
      employees: { running: true, completed: false, error: null }
    }));
    
    try {
      const results = await cleanupEmployees(clientId);
      setCleanupStatus(prev => ({
        ...prev,
        employees: { running: false, completed: true, error: null }
      }));
      console.log('Cleanup results:', results);
      await loadMigrationCounts();
    } catch (error) {
      setCleanupStatus(prev => ({
        ...prev,
        employees: { running: false, completed: false, error: error.message }
      }));
    }
  };

  const handleCleanupLocationEmployees = async () => {
    if (!window.confirm('Are you sure you want to delete ALL LocationEmployee records? This cannot be undone!')) {
      return;
    }
    
    setCleanupStatus(prev => ({
      ...prev,
      locationEmployees: { running: true, completed: false, error: null }
    }));
    
    try {
      const results = await cleanupLocationEmployees(clientId);
      setCleanupStatus(prev => ({
        ...prev,
        locationEmployees: { running: false, completed: true, error: null }
      }));
      console.log('Cleanup results:', results);
      await loadMigrationCounts();
    } catch (error) {
      setCleanupStatus(prev => ({
        ...prev,
        locationEmployees: { running: false, completed: false, error: error.message }
      }));
    }
  };

  const handleCleanupOrgUnitEmployees = async () => {
    if (!window.confirm('Are you sure you want to delete ALL OrgUnitEmployee records? This cannot be undone!')) {
      return;
    }
    
    setCleanupStatus(prev => ({
      ...prev,
      orgUnitEmployees: { running: true, completed: false, error: null }
    }));
    
    try {
      const results = await cleanupOrgUnitEmployees(clientId);
      setCleanupStatus(prev => ({
        ...prev,
        orgUnitEmployees: { running: false, completed: true, error: null }
      }));
      console.log('Cleanup results:', results);
      await loadMigrationCounts();
    } catch (error) {
      setCleanupStatus(prev => ({
        ...prev,
        orgUnitEmployees: { running: false, completed: false, error: error.message }
      }));
    }
  };

  const handleCleanupAllTables = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL records from ALL tables!\n\nAre you absolutely sure?')) {
      return;
    }
    
    const confirmation = window.prompt('⚠️ FINAL WARNING: This action CANNOT be undone!\n\nType "DELETE ALL" to confirm:');
    if (confirmation !== 'DELETE ALL') {
      console.log('Cleanup cancelled - confirmation text did not match');
      return;
    }
    
    try {
      const results = await cleanupAllTables(clientId);
      console.log('Cleanup all tables results:', results);
      
      // Reset all statuses
      setCleanupStatus({
        locations: { running: false, completed: true, error: null },
        laborTargets: { running: false, completed: true, error: null },
        hierarchyChangeLog: { running: false, completed: true, error: null },
        orgUnits: { running: false, completed: true, error: null },
        roles: { running: false, completed: true, error: null },
        employees: { running: false, completed: true, error: null },
        locationEmployees: { running: false, completed: true, error: null },
        orgUnitEmployees: { running: false, completed: true, error: null }
      });
      
      // Reset migration status
      setIndividualMigrationStatus({
        locations: { running: false, completed: false, error: null, results: null },
        laborTargets: { running: false, completed: false, error: null, results: null },
        history: { running: false, completed: false, error: null, results: null },
        metadataCleanup: { running: false, completed: false, error: null, results: null },
        roles: { running: false, completed: false, error: null, results: null },
        employees: { running: false, completed: false, error: null, results: null },
        locationEmployees: { running: false, completed: false, error: null, results: null },
        orgUnitEmployees: { running: false, completed: false, error: null, results: null }
      });
      
      await loadMigrationCounts();
    } catch (error) {
      console.error('Failed to cleanup all tables:', error);
    }
  };

  // Main render
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box maxWidth="1200px" mx="auto" p={3}>
        {loading && activeStep !== -1 && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography variant="h6">Loading data...</Typography>
          </Box>
        )}
        
        {/* Render migration step directly */}
        {!loading && renderMigrationStep()}

      </Box>
    </DashboardLayout>
  );
};

export default StoreMigrationManager;